<template>
  <main @scroll="scroll">
    <div class="interactions"
         id="interactions"
         :style="{'margin-left': marginLeft}">
      <template v-for="interaction in interactions">
        <app-interaction :ref="interaction.id"
                         :key="interaction.id + '-' + interaction.tipo"
                         :data-interaction="interaction"
                         :data-mode="tab ? tab.value : 'LOADING'"
                         :data-active="active === interaction && isDesignTab"
                         :data-removable="interaction.posicaoX !== 1 && isDesignTab"
                         :data-show-placeholder="isDesignTab"
                         @remove="pendingRemoveId = interaction.id"
                         @click="selectInteraction(interaction)"></app-interaction>
      </template>

      <template v-if="isDesignTab">
        <template v-for="position in buttonPositions">
          <app-new-screen-button :data-col="position.x + 1"
                                 :data-row="position.y + 1"
                                 :key="`bt${position.x}${position.y}`"
                                 @click="create"></app-new-screen-button>
        </template>

        <app-side-panel v-if="active !== null"
                        ref="panel"
                        :key="'panel' + active.id"
                        :data-interaction="active"
                        :data-advertising-id="parentId"></app-side-panel>
      </template>
    </div>

    <app-confirmation-modal :value="!!pendingRemoveId"
                            confirm-label="Remover"
                            @confirm="confirmRemove"
                            @cancel="pendingRemoveId = null">
      <span slot="heading">
        {{$t('message.campaigns.message.removeInteraction')}}
      </span>

      <span>
        {{$t('message.campaigns.message.removeInteractionConfirmation')}}
        <br>
        {{$t('message.campaigns.message.settingsWillBeLost')}}
      </span>
    </app-confirmation-modal>

    <div></div>
  </main>
</template>

<script>
    import AppInteraction from './components/interaction/Interaction'
    import AppNewScreenButton from './components/NewScreenButton'
    import AppSidePanel from './components/SidePanel/SidePanel'
    import AppConfirmationModal from '@components/ConfirmationModal'
    import _ from 'lodash'
    import overlap from '@packages/overlap'
    import EventBus from '@packages/event-bus'

    export default {
        components: {
            AppInteraction,
            AppNewScreenButton,
            AppSidePanel,
            AppConfirmationModal,
        },

        metaInfo: {
            script: [
                {
                    src: 'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
                    crossOrigin: 'anonymous',
                    async: true
                },  
                
            ],
        },

        data() {
            return {
                active: null,
                pendingRemoveId: null,
                marginLeft: 0,
                marginTimeout: null,
                integrations: undefined,
                drag: false
            }
        },

        computed: {
            colorPicker() {
                return this.$store.state.interactions.colorPicker
            },
            advertising() {
                return this.$store.state.advertising.single
            },

            parentId() {
                if (!this.$route.params || !this.$route.params.id)
                    return null

                return this.$route.params.id
            },

            interactions() {
                return this.$store.state.interactions.list || []
            },

            x() {
                if (this.interactions.length === 0)
                    return 0

                let max = _.maxBy(this.interactions, (interaction) => {
                    return interaction.posicaoX
                }).posicaoX

                if (max < 3)
                    max = max + 1

                return max
            },

            y() {
                if (this.interactions.length === 0)
                    return 0

                return _.maxBy(this.interactions, (interaction) => {
                    return interaction.posicaoY
                }).posicaoY + 1
            },

            grid() {
                const grid = []

                if (this.x === 0 || this.y === 0)
                    return grid

                for (let x = 0; x < this.x; x++) {
                    grid.push([])

                    for (let y = 0; y < this.y; y++) {
                        grid[x].push(0)
                    }
                }

                _.forEach(this.interactions, (interaction) => {
                    let x = interaction.posicaoX - 1
                    let y = interaction.posicaoY - 1

                    grid[x][y] = 1
                })

                return grid
            },

            positions() {
                let positions = []
                _.forEach(this.grid, (row, x) => {
                    _.forEach(row, (col, y) => {
                        positions.push({x, y, value: this.grid[x][y]})
                    })
                })
                return positions
            },

            buttonPositions() {
                const buttons = []

                _.forEach(this.positions, (pos) => {
                    if (this.isPositionValid({x: pos.x, y: pos.y}))
                        buttons.push({x: pos.x, y: pos.y})
                })

                return buttons
            },

            tab() {
                return this.$store.state.interactions.tab
            },

            isDesignTab() {
                if (!this.tab)
                    return false

                return this.tab.value === 'DESIGN'
            },
        },

        methods: {
            async load() {
                await this.$loader.integrations.load();
                return await this.$loader.interactions.load(this.parentId)
            },

            getInteractionNumber(evt) {
                const number = evt.row > 1 ? `${evt.col} x ${evt.row}` : evt.col;

                return number;
            },

            async create(evt) {
                const data = {
                    posicaoX: evt.col,
                    posicaoY: evt.row,
                    nome: `${this.$t('message.campaigns.message.interaction')} ${this.getInteractionNumber(evt)}`
                }
                await this.$api.interactions.create({
                    advertisingId: this.parentId,
                    data,
                })
                await this.load()
            },

            async confirmRemove() {              

                await this.$api.interactions
                    .remove({
                        id: this.pendingRemoveId,
                        advertisingId: this.parentId
                    })

                this.active = null
                this.pendingRemoveId = null

                await this.load()
            },

            isPositionValid({x, y}) {
                if (x === 0)
                    return false

                if (this.grid[x][y] === 1)
                    return false

                if (this.grid[x][y - 1] === 0)
                    return false

                return true
            },

            selectInteraction(interaction) {
                const isSameInteraction = this.active === interaction;

                this.active = isSameInteraction ? null : interaction

                if (!this.active)
                    return this.marginLeft = 0
            },

            calcMarginLeft() {
                if (!this.active)
                    return this.marginLeft = 0

                const interaction = this.$refs[this.active.id][0].$el
                const panel = this.$refs.panel.$el

                if (!overlap(interaction, panel))
                    return this.marginLeft = 0

                const interactionRect = interaction.getBoundingClientRect()
                const panelRect = panel.getBoundingClientRect()

                this.marginLeft = `-${interactionRect.right - panelRect.left + 50}px`
            },
            scrollHandler() {
                if (this.marginLeft !== 0)
                    return

                if (this.marginTimeout)
                    clearTimeout(this.marginTimeout)

                this.marginTimeout = setTimeout(this.calcMarginLeft, 50)
            },
            clickHandler() {
                this.active = null
            },
            dragToTrue() {
                this.drag = true
            },
            dragToFalse() {
                this.drag = false
            },
            closeSidePanel(event) {                
                if(event.target.id === "interactions" && !this.drag) {
                    this.clickHandler()
                }
            },
            scroll() {
                EventBus.$emit("SCROLL");
            },
        },

        async mounted() {
            EventBus.$on('SCROLL', this.scrollHandler)

            await this.load()
            await this.$loader.advertising.find(this.parentId)
        },

        beforeDestroy() {
            EventBus.$off('SCROLL', this.scrollHandler)
            this.$store.commit('SET_INTERACTIONS', [])
        },

        watch: {
            active(newValue, oldValue) {
                if(newValue) {
                    document.addEventListener('mousedown', this.dragToFalse);      
                    document.addEventListener('mousemove', this.dragToTrue);            
                    document.addEventListener('mouseup', this.closeSidePanel);
                }    

                if (!newValue) {
                    document.removeEventListener('mousedown', this.dragToFalse);      
                    document.removeEventListener('mousemove', this.dragToTrue);            
                    document.removeEventListener('mouseup', this.closeSidePanel);
                    
                    return this.marginLeft = 0
                }

                if (!oldValue || newValue.posicaoX !== oldValue.posicaoX)
                    this.$nextTick(this.calcMarginLeft)
            },
            tab(tab) {
                if(tab.value === "LINK")
                    this.active = null
            }	
        }
    }
</script>

<style lang="scss" scoped>
.interactions {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 337px);
  grid-column-gap: 50px;
  grid-row-gap: 50px;
  transition: all 0.2s;
  padding: 8px 0 30px 10px;

  &.shift-left {
    margin-left: -230px;
  }
}
</style>
